import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/shadcn/dialog'
import { useBrand } from '@/context/brandContext'
import { trpc } from '@/config/trpc'
import { toast } from 'sonner'
import { Loader2, Trash2 } from 'lucide-react'
import { useUserContext } from '@/context/userLogin'
import { LANGUAGE_SUGGESTIONS } from '@/lib/onboarding/languages'

export default function BrandSettingsPage() {
  const navigate = useNavigate()
  const { currentBrand, brands } = useBrand()
  const { updateSessionToken } = useUserContext()
  const utils = trpc.useUtils()

  const [name, setName] = useState(currentBrand?.name ?? '')
  const [language, setLanguage] = useState(currentBrand?.language ?? '')
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Sync fields when currentBrand changes (e.g., after switching brands)
  useEffect(() => {
    if (currentBrand?.name) {
      setName(currentBrand.name)
    }
  }, [currentBrand?.name])
  useEffect(() => {
    setLanguage(currentBrand?.language ?? '')
  }, [currentBrand?.language])

  const { mutateAsync: updateBrand } = trpc.brand.update.useMutation()
  const { mutateAsync: deleteBrand } = trpc.brand.delete.useMutation()

  const canDelete = brands.length > 1

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Please enter a brand name')
      return
    }

    const nextLanguage = language.trim()
    const nameChanged = trimmedName !== currentBrand?.name
    const languageChanged = nextLanguage !== (currentBrand?.language ?? '')

    if (!nameChanged && !languageChanged) {
      toast.info('No changes to save')
      return
    }

    setIsUpdating(true)
    try {
      await updateBrand({
        ...(nameChanged ? { name: trimmedName } : {}),
        // Empty clears it (back to topic-inferred). null is the explicit clear.
        ...(languageChanged ? { language: nextLanguage || null } : {}),
      })
      utils.brand.list.invalidate()
      utils.brand.getCurrent.invalidate()
      toast.success('Brand updated')
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('Failed to update brand')
      }
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      const result = await deleteBrand({ confirm: true })
      if (result.sessionToken) {
        updateSessionToken(result.sessionToken)
      }
      utils.invalidate()
      toast.success(`Brand deleted. Switched to ${result.switchedTo.name}`)
      navigate('/')
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('Failed to delete brand')
      }
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
    }
  }

  if (!currentBrand) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="w-full max-w-lg space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Brand details</CardTitle>
          <CardDescription>Your brand name and the language CASSI writes in.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Brand Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="e.g., My Company"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isUpdating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="language">Post language</Label>
              <p className="text-muted-foreground text-xs">
                CASSI writes posts, briefs, and ideas in this language. Leave empty to match each
                topic.
              </p>
              <div className="flex flex-wrap gap-2">
                {LANGUAGE_SUGGESTIONS.map((l) => (
                  <button
                    type="button"
                    key={l}
                    onClick={() => setLanguage(l)}
                    disabled={isUpdating}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                      language.trim() === l
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border text-foreground hover:bg-hover'
                    }`}>
                    {l}
                  </button>
                ))}
              </div>
              <Input
                id="language"
                type="text"
                placeholder="e.g., English (leave empty to match topic)"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isUpdating}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-red-500/50">
        <CardHeader>
          <CardTitle className="text-lg text-red-500">Danger Zone</CardTitle>
          <CardDescription>
            {canDelete
              ? 'Deleting a brand will permanently remove all its data including moodboards, workspaces, and posts.'
              : 'You cannot delete your only brand. Create another one first.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="w-full border-red-500 text-red-500 hover:bg-red-500/10"
                disabled={!canDelete || isDeleting}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Brand
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Are you absolutely sure?</DialogTitle>
                <DialogDescription>
                  This will permanently delete <strong>{currentBrand.name}</strong> and all its data
                  including moodboards, workspaces, scheduled posts, and connected accounts. This
                  action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="bg-red-500 text-white hover:bg-red-600">
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Brand'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  )
}
